/**
 * Pure parts of the rental printouts (P4-g): the shapes of the frozen snapshots a protocol carries (the API types them as
 * loose records, so they are read defensively here), and the wording helpers. The printouts are built from these snapshots
 * and the reservation — never from the live customer / firma records (decision 6), so a reprint after the customer or the
 * firma changed still says what was signed.
 */

import type { PredaniSeznam, PujcovnaPredani } from './api';
import { PALIVO, CISTOTA, ZAPACH, KAUCE_ZPUSOB } from './predani-helpers';

export interface DokladSnap {
  typ: 'op' | 'rp' | 'pas';
  cislo: string;
  datum_vydani: string | null;
  datum_platnosti: string | null;
}

export interface RidicSnap {
  id_ridice: number;
  jmeno: string;
  prijmeni: string;
  datum_narozeni: string | null;
  telefon: string;
  email: string;
  hlavni: boolean;
  doklady: DokladSnap[];
}

export interface NajemceSnap {
  jmeno: string;
  ulice: string;
  cislo_popisne: string;
  psc: string;
  mesto: string;
  ico: string;
  dico: string;
  zastupce: string;
  telefon: string;
  email: string;
}

export interface PronajimatelSnap {
  nazev: string;
  ulice: string;
  cp: string;
  mesto: string;
  psc: string;
  ico: string;
  dic: string;
  zapis_or: string;
  banka: string;
  ucet: string;
  pobocka: string;
  kontakt_email: string;
  kontakt_telefon: string;
}

export interface UcetSnap {
  predcisli: string | null;
  cislo: string | null;
  kod_banky: string | null;
  iban: string | null;
}

export interface ZavadaSnap {
  id_dilu: number;
  dil: string;
  zavaznost: 'nizka' | 'stredni' | 'vysoka';
  popis: string;
}

const str = (v: unknown): string => (v == null ? '' : String(v));
const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const nul = (v: unknown): string | null => (v == null || v === '' ? null : String(v));

export function asNajemce(v: unknown): NajemceSnap | null {
  if (!v || typeof v !== 'object') return null;
  const r = rec(v);
  return {
    jmeno: str(r.jmeno),
    ulice: str(r.ulice),
    cislo_popisne: str(r.cislo_popisne),
    psc: str(r.psc),
    mesto: str(r.mesto),
    ico: str(r.ico),
    dico: str(r.dico),
    zastupce: str(r.zastupce),
    telefon: str(r.telefon),
    email: str(r.email),
  };
}

export function asPronajimatel(v: unknown): PronajimatelSnap | null {
  if (!v || typeof v !== 'object') return null;
  const r = rec(v);
  return {
    nazev: str(r.nazev),
    ulice: str(r.ulice),
    cp: str(r.cp),
    mesto: str(r.mesto),
    psc: str(r.psc),
    ico: str(r.ico),
    dic: str(r.dic),
    zapis_or: str(r.zapis_or),
    banka: str(r.banka),
    ucet: str(r.ucet),
    pobocka: str(r.pobocka),
    kontakt_email: str(r.kontakt_email),
    kontakt_telefon: str(r.kontakt_telefon),
  };
}

export function asRidici(v: unknown): RidicSnap[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => {
    const r = rec(x);
    return {
      id_ridice: Number(r.id_ridice ?? 0),
      jmeno: str(r.jmeno),
      prijmeni: str(r.prijmeni),
      datum_narozeni: nul(r.datum_narozeni),
      telefon: str(r.telefon),
      email: str(r.email),
      hlavni: r.hlavni === true,
      doklady: (Array.isArray(r.doklady) ? r.doklady : []).map((d) => {
        const q = rec(d);
        return {
          typ: (q.typ === 'rp' || q.typ === 'pas' ? q.typ : 'op') as DokladSnap['typ'],
          cislo: str(q.cislo),
          datum_vydani: nul(q.datum_vydani),
          datum_platnosti: nul(q.datum_platnosti),
        };
      }),
    };
  });
}

export function asUcet(v: unknown): UcetSnap | null {
  if (!v || typeof v !== 'object') return null;
  const r = rec(v);
  return {
    predcisli: nul(r.predcisli),
    cislo: nul(r.cislo),
    kod_banky: nul(r.kod_banky),
    iban: nul(r.iban),
  };
}

export function asZavady(v: unknown): ZavadaSnap[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => {
    const r = rec(x);
    const z = r.zavaznost === 'vysoka' || r.zavaznost === 'stredni' ? r.zavaznost : 'nizka';
    return {
      id_dilu: Number(r.id_dilu ?? 0),
      dil: str(r.dil),
      zavaznost: z as ZavadaSnap['zavaznost'],
      popis: str(r.popis),
    };
  });
}

/** The protocol of one side from the list the API serves (handover first). */
export const strana = (
  s: PredaniSeznam | undefined,
  typ: 'vydani' | 'vraceni',
): PujcovnaPredani | null => s?.predani.find((p) => p.typ === typ) ?? null;

const novejsi = (a: DokladSnap, b: DokladSnap) =>
  (b.datum_platnosti ?? '').localeCompare(a.datum_platnosti ?? '');

/**
 * Which of several documents the contract prints: the one the handover ACCEPTED — complete (a number) and valid through the agreed
 * end (`konecDen`, `YYYY-MM-DD`), the same test as `chybejiciUdaje` on the server; the latest validity among those. When none
 * qualifies (a handover by an admin's override) the latest validity among the numbered ones, then among all. A newer placeholder
 * without a number therefore never hides the older licence that permitted the handover.
 */
function vyberDoklad(
  kandidati: DokladSnap[],
  konecDen: string | undefined,
  poradi?: (a: DokladSnap, b: DokladSnap) => number,
): DokladSnap | null {
  if (kandidati.length === 0) return null;
  const cislo = (d: DokladSnap) => d.cislo.trim() !== '';
  const platny = (d: DokladSnap) =>
    cislo(d) && d.datum_platnosti != null && (konecDen == null || d.datum_platnosti >= konecDen);
  // the kind preference (an OP before a passport) decides only among documents that qualify; the fallbacks are by validity alone
  const [platne, ocislovane] = [kandidati.filter(platny), kandidati.filter(cislo)];
  if (platne.length > 0)
    return [...platne].sort((a, b) => (poradi?.(a, b) ?? 0) || novejsi(a, b))[0]!;
  for (const skupina of [ocislovane, kandidati]) {
    if (skupina.length > 0) return [...skupina].sort(novejsi)[0]!;
  }
  return null;
}

/** The document of one kind (a driver may hold several): see `vyberDoklad`. Without `konecDen` any validity qualifies. */
export const dokladTypu = (
  r: RidicSnap,
  typ: DokladSnap['typ'],
  konecDen?: string,
): DokladSnap | null =>
  vyberDoklad(
    r.doklady.filter((d) => d.typ === typ),
    konecDen,
  );

/**
 * The main driver's identity document: an OP or a passport — the one the handover accepted (see `vyberDoklad`). An OP wins over a
 * passport when both qualify, so an expired OP next to a valid passport prints the passport.
 */
export const dokladTotoznosti = (r: RidicSnap, konecDen?: string): DokladSnap | null =>
  vyberDoklad(
    r.doklady.filter((d) => d.typ === 'op' || d.typ === 'pas'),
    konecDen,
    (a, b) => (a.typ === b.typ ? 0 : a.typ === 'op' ? -1 : 1),
  );

export const ridicJmeno = (r: Pick<RidicSnap, 'jmeno' | 'prijmeni'>): string =>
  [r.jmeno, r.prijmeni].filter(Boolean).join(' ');

/** `Horská 12` — street and house number as the customer record holds them. */
export const adresaRadek = (n: Pick<NajemceSnap, 'ulice' | 'cislo_popisne'>): string =>
  [n.ulice, n.cislo_popisne].filter(Boolean).join(' ');

/** A Czech bank account `19-2000145399/0800`, or the IBAN when that is all there is. */
export function ucetText(u: UcetSnap | null): string {
  if (!u) return '';
  if (u.cislo)
    return `${u.predcisli ? `${u.predcisli}-` : ''}${u.cislo}${u.kod_banky ? `/${u.kod_banky}` : ''}`;
  return u.iban ?? '';
}

/** `1 den`, `3 dny`, `7 dní`. */
export const dnyText = (n: number): string =>
  `${n} ${n === 1 ? 'den' : n >= 2 && n <= 4 ? 'dny' : 'dní'}`;

const label = <T extends string>(
  list: ReadonlyArray<{ value: T; label: string }>,
  v: string | undefined,
): string | null => list.find((x) => x.value === v)?.label ?? null;

/** The state-of-the-car lines of a protocol side: what was ticked and what was read off (fuel, cleanliness, smell). */
export function stavVozuRadky(
  seznam: PujcovnaPredani['kontrolni_seznam'],
  body: ReadonlyArray<{ key: string; label: string }>,
): Array<{ text: string; ano: boolean | null }> {
  if (!seznam) return [];
  const s = seznam as Record<string, unknown>;
  const out: Array<{ text: string; ano: boolean | null }> = body.map((b) => ({
    text: b.label,
    ano: s[b.key] === true,
  }));
  const palivo = label(PALIVO, s.palivo as string | undefined);
  const cistota = label(CISTOTA, s.cistota as string | undefined);
  const zapach = label(ZAPACH, s.zapach as string | undefined);
  if (palivo) out.push({ text: `Palivo: ${palivo}`, ano: null });
  if (cistota) out.push({ text: `Čistota: ${cistota}`, ano: null });
  if (zapach) out.push({ text: `Zápach: ${zapach}`, ano: null });
  return out;
}

export type PojisteniTypKey = 'povinne_ruceni' | 'havarijni' | 'skla' | 'zavazadla' | 'posadka';

export const POJISTENI_NAZVY: Record<PojisteniTypKey, string> = {
  povinne_ruceni: 'Povinné ručení | Car insurance',
  havarijni: 'Havarijní pojištění | Accident ins.',
  skla: 'Připojištění skel | Glass insurance',
  zavazadla: 'Pojištění zavazadel | Luggage ins.',
  posadka: 'Pojištění posádky | Crew ins.',
};

export interface PojisteniVstup {
  typ: PojisteniTypKey;
  v_cene: boolean;
  limit_plneni: number;
  spoluucast_proc: number;
  spoluucast_kc: number;
}

const kc = (n: number): string =>
  `${new Intl.NumberFormat('cs-CZ').format(n).replace(/\s/g, '\u00a0')}\u00a0Kč`;

const km = (n: number): string => `${new Intl.NumberFormat('cs-CZ').format(n).replace(/\s/g, '\u00a0')}\u00a0km`;

/** What the contract's section 8 (Cena) prints, from the price frozen at the handover. Null = nothing to print (an unpriced rental): the boxes stay for writing in. */
export interface CenaSmlouvy {
  /** "1 800 Kč" or, for a flat package, "Paušál do 4 dní — 20 000 Kč". */
  cenaDen: string;
  dny: number;
  /** The allowance for the whole rental ("800 km"), null = no limit set. */
  limitKm: string | null;
  /** Extra lines between the rent and the total: the abroad surcharge, the discount (only when non-zero). */
  radky: Array<{ k: string; v: string }>;
  /** The lease after the discount, the deposit not included. */
  celkem: string;
}
export function cenaSmlouvy(cena: PujcovnaPredani['cena'] | undefined): CenaSmlouvy | null {
  const r = cena?.rozpis;
  if (!r) return null;
  const radky: Array<{ k: string; v: string }> = [];
  if (r.zahranici > 0) radky.push({ k: 'Příplatek za zahraničí | Abroad surcharge', v: kc(r.zahranici) });
  if (r.sleva_kc > 0) radky.push({ k: 'Sleva | Discount', v: `−${kc(r.sleva_kc)}` });
  return {
    // a flat package is billed at the EFFECTIVE rent (`najem`): the pricing raises it to the car's minimum rent when the package is cheaper
    cenaDen: r.pausal ? `Paušál do ${r.pausal.dny} dní — ${kc(r.najem)}` : kc(r.cena_den),
    dny: r.dny,
    limitKm: cena?.km_povoleno_den == null ? null : km(cena.km_povoleno_den * r.dny),
    radky,
    celkem: kc(r.pronajem),
  };
}

/** The return protocol's line about the lease for the days actually rented; '' = no price. */
export function cenaVraceniText(cena: PujcovnaPredani['cena'] | undefined): string {
  const r = cena?.rozpis;
  return r ? `Pronájem za skutečné dny: ${dnyText(r.dny)} — ${kc(r.pronajem)}.` : '';
}

/** The return protocol's line about the km surcharge; '' = none. */
export function doplatekKmText(cena: PujcovnaPredani['cena'] | undefined): string {
  const d = cena?.doplatek_km;
  if (!d) return '';
  return `Doplatek za nájezd navíc: ${kc(d.castka)} (najeto ${km(d.najeto)}, limit ${km(d.povoleno)}).`;
}

/** The deductible as the legacy contract printed it: `4 %, min. 5 000 Kč`, `5 000 Kč`, `4 %` or `bez spoluúčasti`. */
export function spoluucastText(
  b: Pick<PojisteniVstup, 'spoluucast_proc' | 'spoluucast_kc'>,
): string {
  if (b.spoluucast_proc > 0 && b.spoluucast_kc > 0)
    return `${b.spoluucast_proc} %, min. ${kc(b.spoluucast_kc)}`;
  if (b.spoluucast_proc > 0) return `${b.spoluucast_proc} %`;
  if (b.spoluucast_kc > 0) return kc(b.spoluucast_kc);
  return 'bez spoluúčasti';
}

/** One insurance line of the contract: ANO / NE, the limit and (havarijní, skla) the deductible. */
export function pojisteniRadek(b: PojisteniVstup | undefined): {
  ano: boolean;
  limit: string;
  spoluucast: string;
} {
  if (!b || !b.v_cene) return { ano: false, limit: '', spoluucast: '' };
  const maSpoluucast = b.typ === 'havarijni' || b.typ === 'skla';
  return {
    ano: true,
    limit: b.limit_plneni > 0 ? kc(b.limit_plneni) : '',
    spoluucast: maSpoluucast ? spoluucastText(b) : '',
  };
}

/**
 * The deposit as the protocol recorded it: "ano" / "ne" (an explicit "no" is a fact, not an absent record), then the amount and
 * the method when there are any. '' = nothing was recorded (a technical block, or a protocol from before deposits).
 */
export function kauceText(kauce: PujcovnaPredani['kauce'] | undefined): string {
  if (!kauce) return '';
  const cast: string[] = [kauce.ano ? 'ano' : 'ne'];
  if (kauce.castka != null) cast.push(kc(kauce.castka));
  const zpusob = KAUCE_ZPUSOB.find((z) => z.value === kauce.zpusob)?.label.toLowerCase();
  if (zpusob) cast.push(zpusob);
  return cast.join(' · ');
}

/** What a note may take of the one-sheet protocol: this many characters AND this many lines (a note of one-letter lines is as tall as it is long). */
export const POZNAMKA_ZNAKU = 240;
export const POZNAMKA_RADKU = 5;

/**
 * The note as it fits the one sheet (`text`) and whether it had to be cut (`zkracena`, then the full text goes on the annex).
 * The cut is by characters first, then by lines, and never leaves a half line of the dropped part behind.
 */
export function poznamkaNaListu(
  t: string | null | undefined,
  znaku = POZNAMKA_ZNAKU,
  radku = POZNAMKA_RADKU,
): { text: string; zkracena: boolean } {
  const cely = t ?? '';
  let text = cely;
  if (text.length > znaku) text = text.slice(0, znaku);
  const radky = text.split('\n');
  if (radky.length > radku) text = radky.slice(0, radku).join('\n');
  return text === cely
    ? { text: cely, zkracena: false }
    : { text: `${text.trimEnd()}… (celé znění na příloze)`, zkracena: true };
}

/**
 * A renter is a company (section 1 of the contract) when ANY of IČO, DIČ or a representative is recorded — a private person has none
 * of them, and a company without a Czech IČO still has a DIČ or a representative.
 */
export const jeFirma = (n: Pick<NajemceSnap, 'ico' | 'dico' | 'zastupce'>): boolean =>
  [n.ico, n.dico, n.zastupce].some((v) => v.trim() !== '');

/** The car as the handover froze it (migration 0075). */
export interface VozidloSnap {
  spz: string;
  vin: string;
  znacka: string;
  model: string;
  provedeni: string;
  barva: string;
}

export function asVozidlo(v: unknown): VozidloSnap | null {
  if (!v || typeof v !== 'object') return null;
  const r = rec(v);
  return {
    spz: str(r.spz),
    vin: str(r.vin),
    znacka: str(r.znacka),
    model: str(r.model),
    provedeni: str(r.provedeni),
    barva: str(r.barva),
  };
}

/** The insurance blocks as the handover froze them; null = none were frozen (an older protocol, or a technical block). */
export function asPojisteni(v: unknown): PojisteniVstup[] | null {
  if (!Array.isArray(v)) return null;
  const typy: PojisteniTypKey[] = ['povinne_ruceni', 'havarijni', 'skla', 'zavazadla', 'posadka'];
  return v
    .map((x) => rec(x))
    .filter((r) => typy.includes(r.typ as PojisteniTypKey))
    .map((r) => ({
      typ: r.typ as PojisteniTypKey,
      v_cene: r.v_cene === true,
      limit_plneni: Number(r.limit_plneni ?? 0) || 0,
      spoluucast_proc: Number(r.spoluucast_proc ?? 0) || 0,
      spoluucast_kc: Number(r.spoluucast_kc ?? 0) || 0,
    }));
}

/** The one-sheet protocol lists at most this many faults a side, each cut to a bounded number of rendered lines; the full text is on the annex. */
export const ZAVAD_NA_LISTU = 8;
export const DIL_ZNAKU = 24;
export const POPIS_ZNAKU = 30;

const zkrat = (t: string, n: number) => (t.length <= n ? t : `${t.slice(0, n).trimEnd()}…`);

/** A fault as it fits one row of the one-sheet protocol (at most two rendered lines), and whether anything was cut. */
export function zavadaNaListu(z: Pick<ZavadaSnap, 'dil' | 'popis'>): {
  dil: string;
  popis: string;
  zkracena: boolean;
} {
  const dil = zkrat(z.dil, DIL_ZNAKU);
  const popis = zkrat(z.popis, POPIS_ZNAKU);
  return { dil, popis, zkracena: dil !== z.dil || popis !== z.popis };
}

/** The faults whose full text belongs on the annex: those past the sheet's rows, and those that had to be cut to fit one. */
export const zavadyNaPrilohu = (zavady: readonly ZavadaSnap[]): ZavadaSnap[] =>
  zavady.filter((z, i) => i >= ZAVAD_NA_LISTU || zavadaNaListu(z).zkracena);

/** The reason an admin handed the car over despite missing data: printed on the sheet up to this length, in full on the annex. */
export const OVERRIDE_ZNAKU = 90;
